import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	Heart,
	MapPin,
	Clock,
	Star,
	MessageSquare,
	Calendar,
	ArrowRight,
	Search,
} from "lucide-react";
import { motion } from "framer-motion";
import { DoulaListing } from "@/types";

const ParentFavorites: React.FC = () => {
	const { profile: userProfile } = useAuth();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [favorites, setFavorites] = useState<DoulaListing[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [appointments, setAppointments] = useState<any[]>([]);

	useEffect(() => {
		if (userProfile) {
			fetchFavorites();
			fetchAppointments();
		} else {
			// If no user profile, still show the page but with empty state
			setLoading(false);
		}
	}, [userProfile]);

	const fetchFavorites = async () => {
		if (!userProfile) return;

		try {
			setLoading(true);
			setError(null);

			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-favorites`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				// If the edge function doesn't exist yet (404) or other server error,
				// just show empty state instead of error
				if (response.status === 404 || response.status === 403) {
					setFavorites([]);
					return;
				}
				throw new Error(result.error || "Failed to fetch favorites");
			}

			setFavorites(result.data || []);
		} catch (error: any) {
			setFavorites([]);
		} finally {
			setLoading(false);
		}
	};

	const fetchAppointments = async () => {
		if (!userProfile) return;

		try {
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-parent-appointments`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						parentUserId: userProfile.id,
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				setAppointments([]);
			} else {
				setAppointments(result.data || []);
			}
		} catch (error) {
			setAppointments([]);
		}
	};

	const handleRemoveFavorite = async (doulaListingId: string) => {
		if (!userProfile) return;

		try {
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/toggle-favorite`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						doula_listing_id: doulaListingId,
					}),
				},
			);

			if (response.ok) {
				// Remove from local state
				setFavorites((prev) => prev.filter((fav) => fav.id !== doulaListingId));
			}
		} catch (error) {
			// Silent error handling
		}
	};

	const formatLocation = (doula: DoulaListing) => {
		const parts = [];
		if (doula.location_city) parts.push(doula.location_city);
		if (doula.location_state) parts.push(doula.location_state);
		return parts.length > 0 ? parts.join(", ") : "Location not specified";
	};

	const formatExperience = (doula: DoulaListing) => {
		if (doula.years_experience) {
			return `${doula.years_experience} year${doula.years_experience !== 1 ? "s" : ""} experience`;
		}
		if (doula.births_attended_range) {
			return `${doula.births_attended_range} births attended`;
		}
		return "Experience not specified";
	};

	const formatDate = (dateString: string | null) => {
		if (!dateString) return "Present";
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			year: "numeric",
		});
	};

	const hasAppointmentWithDoula = (doulaUserId: string) => {
		return appointments.some(
			(appointment) => appointment.doula_user_id === doulaUserId,
		);
	};

	const handleMessageDoula = (doulaUserId: string) => {
		// Navigate to messages page
		navigate("/parent-messages");
	};

	return (
		<div className="min-h-screen bg-gray-50">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					className="mb-8"
				>
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
						<div>
							<h1 className="text-3xl font-bold text-gray-900 font-karla mb-2">
								Your Favorite Doulas
							</h1>
							<p className="text-gray-600 font-karla">
								{favorites.length === 0
									? "You haven't favorited any doulas yet"
									: `${favorites.length} doula${favorites.length !== 1 ? "s" : ""} in your favorites`}
							</p>
						</div>
						<Button
							onClick={() => navigate("/search-doulas")}
							className="bg-primary-500 hover:bg-primary-600 text-white"
						>
							<Search className="h-4 w-4 mr-2" />
							Find More Doulas
						</Button>
					</div>
				</motion.div>

				{/* Favorites Grid */}
				{favorites.length === 0 ? (
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="max-w-2xl mx-auto"
					>
						<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100">
							<CardContent className="p-8 text-center">
								<div className="w-20 h-20 bg-primary-200 rounded-full flex items-center justify-center mx-auto mb-6">
									<Heart className="h-10 w-10 text-primary-600" />
								</div>
								<h2 className="text-2xl font-semibold text-gray-900 font-karla mb-4">
									No favorites yet
								</h2>
								<p className="text-gray-600 font-karla mb-8 leading-relaxed">
									Start exploring doulas to find your perfect match. When you
									find someone you like, click the heart icon to save them to
									your favorites.
								</p>
								<div className="space-y-4">
									<Button
										onClick={() => navigate("/search-doulas")}
										className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-3"
									>
										<Search className="h-5 w-5 mr-2" />
										Browse Doulas
									</Button>
									<p className="text-sm text-gray-500 font-karla">
										Your favorites will appear here once you start exploring
									</p>
								</div>
							</CardContent>
						</Card>
					</motion.div>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{favorites.map((doula, index) => (
							<motion.div
								key={doula.id}
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
							>
								<Card className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group">
									<CardContent className="p-6">
										{/* Header */}
										<div className="flex items-start justify-between mb-4">
											<div className="flex items-center space-x-3">
												<div className="w-12 h-12 rounded-lg overflow-hidden bg-primary-100 flex items-center justify-center">
													{doula.user?.avatar_url ? (
														<img
															src={doula.user.avatar_url}
															alt={doula.user.full_name}
															className="w-full h-full object-cover"
														/>
													) : (
														<div className="w-full h-full bg-primary-100 flex items-center justify-center">
															<span className="text-primary-600 font-semibold text-lg">
																{doula.user?.full_name?.charAt(0) || "D"}
															</span>
														</div>
													)}
												</div>
												<div>
													<h3 className="font-semibold text-gray-900 font-karla text-lg">
														{doula.user?.full_name}
													</h3>
													<p className="text-sm text-gray-600 font-karla">
														{formatExperience(doula)}
													</p>
												</div>
											</div>
											<Button
												variant="ghost"
												size="sm"
												onClick={(e) => {
													e.stopPropagation();
													handleRemoveFavorite(doula.id);
												}}
												className="text-red-500 hover:text-red-700 hover:bg-red-50"
											>
												<Heart className="h-5 w-5 fill-current" />
											</Button>
										</div>

										{/* Bio */}
										{doula.bio && (
											<p className="text-gray-600 font-karla mb-4 line-clamp-3">
												{doula.bio}
											</p>
										)}

										{/* Location */}
										<div className="flex items-center text-sm text-gray-600 mb-4">
											<MapPin className="h-4 w-4 mr-1" />
											<span className="font-karla">
												{formatLocation(doula)}
											</span>
										</div>

										{/* Packages */}
										{doula.packages && doula.packages.length > 0 && (
											<div className="mb-4">
												<p className="text-sm font-semibold text-gray-900 font-karla mb-2">
													Packages ({doula.packages.length})
												</p>
												<div className="space-y-2">
													{doula.packages.slice(0, 2).map((pkg) => (
														<div
															key={pkg.id}
															className="flex items-center justify-between text-sm"
														>
															<span className="font-karla">{pkg.name}</span>
															<span className="font-semibold text-primary-600">
																${pkg.price}
															</span>
														</div>
													))}
													{doula.packages.length > 2 && (
														<p className="text-xs text-gray-500 font-karla">
															+{doula.packages.length - 2} more packages
														</p>
													)}
												</div>
											</div>
										)}

										{/* Certifications */}
										{doula.certifications &&
											doula.certifications.length > 0 && (
												<div className="mb-4">
													<p className="text-sm font-semibold text-gray-900 font-karla mb-2">
														Certifications
													</p>
													<div className="flex flex-wrap gap-1">
														{doula.certifications.slice(0, 3).map((cert) => (
															<Badge
																key={cert.id}
																variant="secondary"
																className="text-xs bg-primary-100 text-primary-800 font-karla"
															>
																{cert.name}
															</Badge>
														))}
														{doula.certifications.length > 3 && (
															<Badge
																variant="secondary"
																className="text-xs bg-gray-100 text-gray-600 font-karla"
															>
																+{doula.certifications.length - 3} more
															</Badge>
														)}
													</div>
												</div>
											)}

										{/* Action Buttons */}
										<div className="grid grid-cols-2 gap-2 pt-4 border-t border-gray-100">
											<Button
												onClick={() => navigate(`/doula-profile/${doula.id}`)}
												className="bg-primary-500 hover:bg-primary-600 text-white font-karla"
											>
												<ArrowRight className="h-4 w-4 mr-2" />
												View Profile
											</Button>
											<Button
												onClick={() => handleMessageDoula(doula.user?.id || "")}
												disabled={
													!hasAppointmentWithDoula(doula.user?.id || "")
												}
												variant="outline"
												className={`font-karla ${
													hasAppointmentWithDoula(doula.user?.id || "")
														? "border-primary-200 text-primary-700 hover:bg-primary-50"
														: "border-gray-200 text-gray-400 cursor-not-allowed"
												}`}
											>
												<MessageSquare className="h-4 w-4 mr-2" />
												Message
											</Button>
										</div>
									</CardContent>
								</Card>
							</motion.div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default ParentFavorites;
